import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import useWindowSize from '@shared/hooks/windowSize'
import MyImage from '@shared/components/myImage'
import arrowNext from '@assets/images/icon/arrow-circle-left.svg'
import arrowPrev from '@assets/images/icon/arrow-circle-right.svg'
import styles from './style.module.scss'

function ShortsSlider({
  children,
  gap = 15,
  nav,
  single,
  destroyBelow = 0,
  vertical
}) {
  const [btn, setBtn] = useState({ next: nav, prev: false })
  const ref = useRef()
  const [size] = useWindowSize()

  function handleNext() {
    if (vertical) {
      ref.current.scrollTop += getItemHeight()
    } else {
      ref.current.scrollLeft += getItemWidth()
    }
  }

  function handlePrev() {
    if (vertical) {
      ref.current.scrollTop -= getItemHeight()
    } else {
      ref.current.scrollLeft -= getItemWidth()
    }
  }

  function handleScroll() {
    const container = ref.current
    if (nav) {
      const scrollPosition = vertical ? container.scrollHeight - container.clientHeight : container.scrollWidth - container.clientWidth
      setBtn({ next: scrollPosition !== getScrollPosition(), prev: getScrollPosition() > 0 })
    }
  }

  const getItemWidth = () => ref?.current?.childNodes[0]?.clientWidth + gap
  const getItemHeight = () => ref?.current?.childNodes[0]?.clientHeight + gap
  const getScrollPosition = () => (vertical ? ref.current.scrollTop : ref.current.scrollLeft)

  useEffect(() => {
    if (!single) {
      const slider = ref.current
      let isDown = false
      let start
      let scroll

      const handleMouseDown = (e) => {
        isDown = true
        slider.classList.add('active')
        start = vertical ? e.pageY - slider.offsetTop : e.pageX - slider.offsetLeft
        scroll = vertical ? slider.scrollTop : slider.scrollLeft
      }

      const handleMouseLeave = () => {
        isDown = false
        slider.classList.remove('active')
      }

      const handleMouseUp = () => {
        isDown = false
        slider.classList.remove('active')
      }

      const handleMouseMove = (e) => {
        if (!isDown) return
        e.preventDefault()
        const current = vertical ? e.pageY - slider.offsetTop : e.pageX - slider.offsetLeft
        const distance = (current - start) * 1 // scroll-fast
        if (vertical) {
          slider.scrollTop = scroll - distance
        } else {
          slider.scrollLeft = scroll - distance
        }
      }

      slider.addEventListener('mousedown', handleMouseDown)
      slider.addEventListener('mouseleave', handleMouseLeave)
      slider.addEventListener('mouseup', handleMouseUp)
      slider.addEventListener('mousemove', handleMouseMove)

      return () => {
        slider.removeEventListener('mousedown', handleMouseDown)
        slider.removeEventListener('mouseleave', handleMouseLeave)
        slider.removeEventListener('mouseup', handleMouseUp)
        slider.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [single, vertical])

  useEffect(() => {
    handleScroll()
  }, [children, vertical])

  return (
    <div
      className={`${styles.slider} ${destroyBelow > size ? styles.destroy : ''} position-relative`}
    >
      {btn?.prev && size > 767 && (
        <div
          className={`${styles.arrow} ${styles.prev}`}
          onClick={handlePrev}
        >
          <MyImage src={arrowPrev} alt='prev' />
        </div>
      )}
      <div
        className={`${styles.inner} slider-track d-flex ${vertical && 'flex-column'}`}
        style={{ gap: gap }}
        onScroll={handleScroll}
        ref={ref}
        id='s-slider'
      >
        {children}
      </div>
      {
        btn?.next && size > 767 && (
          <div
            className={`${styles.arrow} ${styles.next}`}
            onClick={handleNext}
          >
            <MyImage src={arrowNext} alt='next' />
          </div>
        )
      }
    </div>
  )
}

ShortsSlider.propTypes = {
  children: PropTypes.node,
  gap: PropTypes.number,
  nav: PropTypes.bool,
  single: PropTypes.bool,
  destroyBelow: PropTypes.number,
  vertical: PropTypes.bool
}

export default ShortsSlider
